// @ts-check

import { motion } from 'framer-motion'
import { DeepResearch } from "../ui/deep-research"
import { Brain, ListTodo } from 'lucide-react';
import { ChatMarkdownBase } from "./ChatBubble";
import ChatBubbleTool from "./ChatBubbleTool";
import { BASE_DEEPRESEARCH_AGENT_ID } from "./utils";

const DeepResearchStep = /**@type{const}*/({
  REASONING: "reasoning",
  TOOL_CALL: "tool-call",
  SUB_AGENT: "sub-agent",
})

/** @param {import("./utils").NormalisedChatMessagePart<keyof import("./utils").NormalisedChatMessagePartDataMap>[]} normalisedParts */
function getDeepResearchSteps(normalisedParts) {
  const steps = []
  const activeDelegations = new Map()  // agent_id -> step

  for (const part of normalisedParts) {
    const agentId = part.agent_id || BASE_DEEPRESEARCH_AGENT_ID

    if (part.type === "reasoning_step") {
      if (agentId === BASE_DEEPRESEARCH_AGENT_ID) {
        steps.push({
          type: DeepResearchStep.REASONING,
          part: part
        })
      } else {
        if (!activeDelegations.has(agentId)) continue
        const delegationStep = activeDelegations.get(agentId)
        delegationStep.subParts.push(part)
      }
    }
    else if (part.type === "sub_agent") {
      const subAgentStep = {
        type: DeepResearchStep.SUB_AGENT,
        part: part,
        subParts: []
      }

      steps.push(subAgentStep)
      activeDelegations.set(agentId, subAgentStep)
    }
    else if (part.type === "tool_call") {
      if (agentId !== BASE_DEEPRESEARCH_AGENT_ID) {
        if (!activeDelegations.has(agentId)) continue
        // Add to delegation's subParts
        const delegationStep = activeDelegations.get(agentId)
        delegationStep.subParts.push(part)
      } else {
        // Standalone tool call step for base agent
        steps.push({
          type: DeepResearchStep.TOOL_CALL,
          part: part
        })
      }
    } else {
      if (agentId === BASE_DEEPRESEARCH_AGENT_ID) continue
      if (!activeDelegations.has(agentId)) continue

      const delegationStep = activeDelegations.get(agentId)
      delegationStep.subParts.push(part)
    }
  }

  // console.log({normalisedParts, steps})
  return steps
}

/**
 * @param {Object} props
 * @param {import("./utils").NormalisedChatMessagePart<keyof import("./utils").NormalisedChatMessagePartDataMap>[]} props.normalisedParts
 * @param {boolean} [props.isStreaming] - Whether the deep research is streaming
 */
function ChatBubbleDeepResearch({ normalisedParts, isStreaming = false }) {
  const steps = getDeepResearchSteps(normalisedParts)

  if (steps.length === 0) return null

  return (
    <DeepResearch className="max-h-[30rem] mb-8" shouldScrollToBottom={isStreaming}>
      {steps.map((step, index) => {
        if (step.type === DeepResearchStep.REASONING) {
          return (
            <DeepResearch.Step status="completed" key={index}>
              <DeepResearch.StepHeader>
                <DeepResearch.StepIcon>
                  <Brain />
                </DeepResearch.StepIcon>
                <DeepResearch.StepTitle>{step.part.data.title}</DeepResearch.StepTitle>
              </DeepResearch.StepHeader>

              <DeepResearch.StepContent>
                <ChatMarkdownBase className="prose prose-sm dark:prose-invert max-w-none">
                  {step.part.data.content}
                </ChatMarkdownBase>
              </DeepResearch.StepContent>
            </DeepResearch.Step>
          )
        }

        if (step.type === DeepResearchStep.SUB_AGENT) {
          return (
            <DeepResearch.Step status={step.part.data.status} key={index}>
              <DeepResearch.StepHeader>
                <DeepResearch.StepIcon>
                  <ListTodo />
                </DeepResearch.StepIcon>
                <DeepResearch.StepTitle>{step.part.data.task}</DeepResearch.StepTitle>
              </DeepResearch.StepHeader>

              {step.subParts && step.subParts.length > 0 && (
                <DeepResearch.StepContent>
                  {step.subParts.map((subPart, subIndex) => (
                    <motion.div layout>
                      {subPart.type === "reasoning_step"
                        ? (
                          <ChatMarkdownBase
                            key={subIndex}
                            className="prose prose-sm dark:prose-invert max-w-none"
                          >
                            {subPart.data.content}
                          </ChatMarkdownBase>
                        ) : subPart.type === "tool_call" ? (
                          <ChatBubbleTool
                            key={subIndex}
                            name={subPart.data.name}
                            args={subPart.data.args}
                            result={subPart.data.result}
                          />
                        ) : null}
                    </motion.div>
                  ))}
                </DeepResearch.StepContent>
              )}
            </DeepResearch.Step>
          )
        }

        if (step.type === DeepResearchStep.TOOL_CALL) {
          const status = step.part.data.result != null ? "completed" : "pending"
          return (
            <DeepResearch.Step status={status} key={index}>
              <DeepResearch.StepHeader>
                <ChatBubbleTool
                  name={step.part.data.name}
                  args={step.part.data.args}
                  result={step.part.data.result}
                />
              </DeepResearch.StepHeader>
            </DeepResearch.Step>
          )
        }

        throw new Error(`Unknown step type: ${step.type}`)
      })}
    </DeepResearch>
  )
}

export default ChatBubbleDeepResearch
